import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { COURSE_BY_ID } from '../../gql/queries/academyQuery'
import { MARK_LESSON_COMPLETE } from '../../gql/mutations/academyMutations'
import {
  ICourseByIdResponse,
  ILesson,
  IMarkLessonCompleteResponse,
} from '../../types/academy'
import { ResourceContentViewer } from '../resourceCenter/components/ResourceContentViewer'
import { useDisclourse } from '../../hooks/useDisclourse'
import { QuizModal } from './components/QuizModal'

export const CoursePlayerPage = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const { onOpen: onOpenQuiz, isOpen: isOpenQuiz, onClose: onCloseQuiz } = useDisclourse()

  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery<ICourseByIdResponse>(COURSE_BY_ID, {
    variables: { id: Number(courseId) },
    fetchPolicy: 'network-only',
    skip: !courseId,
  })

  const course = data?.courseById
  const lessons = useMemo(() => course?.lessons || [], [course])

  // Al cargar, posicionarse en la primera lección no completada (o la primera)
  useEffect(() => {
    if (!currentLessonId && lessons.length > 0) {
      const firstIncomplete = lessons.find(
        (lesson) => lesson.myProgress?.status !== 'completed'
      )
      setCurrentLessonId((firstIncomplete || lessons[0]).id)
    }
  }, [lessons, currentLessonId])

  const currentLesson: ILesson | undefined = lessons.find((l) => l.id === currentLessonId)
  const currentIndex = lessons.findIndex((l) => l.id === currentLessonId)

  const [markLessonComplete, { loading: marking }] =
    useMutation<IMarkLessonCompleteResponse>(MARK_LESSON_COMPLETE)

  const handleMarkComplete = async (lesson: ILesson) => {
    try {
      const result = await markLessonComplete({ variables: { lessonId: Number(lesson.id) } })
      if (result.data?.markLessonComplete.success) {
        toast.success('Lesson completed!')
        refetch()
      } else if (result.data?.markLessonComplete.errors?.length) {
        toast.error(`Error: ${result.data.markLessonComplete.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error marking lesson complete:', err)
      toast.error('Error marking lesson complete')
    }
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading course: {error.message}
      </div>
    )
  }

  if (loading || !course) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  const isCompleted = (lesson: ILesson) => lesson.myProgress?.status === 'completed'

  return (
    <div className="container-fluid px-4 py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item">
                <Link to="/academy" className="text-decoration-none">
                  Academy
                </Link>
              </li>
              <li className="breadcrumb-item active">{course.name}</li>
            </ol>
          </nav>
          <h1 className="h4 mb-0">{course.name}</h1>
        </div>
        <div style={{ minWidth: 220 }}>
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>Progress</span>
            <span>{course.completionPercentage}%</span>
          </div>
          <div className="progress" style={{ height: 8 }}>
            <div
              className={`progress-bar ${
                course.completionPercentage === 100 ? 'bg-success' : 'bg-primary'
              }`}
              role="progressbar"
              style={{ width: `${course.completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Lesson list */}
        <div className="col-12 col-md-4 col-lg-3">
          <div className="card h-100">
            <div className="card-header py-3">
              <h6 className="mb-0">
                <i className="bi bi-collection-play me-2" />
                Lessons ({lessons.length})
              </h6>
            </div>
            <div className="list-group list-group-flush" style={{ overflowY: 'auto' }}>
              {lessons.map((lesson, idx) => (
                <button
                  key={lesson.id}
                  type="button"
                  className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${
                    lesson.id === currentLessonId ? 'active' : ''
                  }`}
                  onClick={() => setCurrentLessonId(lesson.id)}
                >
                  <i
                    className={`bi ${
                      isCompleted(lesson)
                        ? 'bi-check-circle-fill text-success'
                        : 'bi-circle'
                    }`}
                  />
                  <span className="flex-fill text-truncate text-start">
                    {idx + 1}. {lesson.displayTitle}
                  </span>
                  {lesson.hasQuiz && <i className="bi bi-patch-question" title="Has quiz" />}
                </button>
              ))}
              {lessons.length === 0 && (
                <div className="list-group-item text-muted text-center small py-4">
                  This course has no lessons yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lesson content */}
        <div className="col-12 col-md-8 col-lg-9">
          {currentLesson ? (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
                <h5 className="mb-0">{currentLesson.displayTitle}</h5>
                <div className="d-flex gap-2">
                  {currentLesson.hasQuiz && (
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={onOpenQuiz}>
                      <i className="bi bi-patch-question me-1" /> Take Quiz
                    </button>
                  )}
                  {isCompleted(currentLesson) ? (
                    <span className="badge bg-success d-flex align-items-center gap-1 px-3">
                      <i className="bi bi-check-circle" /> Completed
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      disabled={marking}
                      onClick={() => handleMarkComplete(currentLesson)}
                    >
                      <i className="bi bi-check-lg me-1" /> Mark as Complete
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body" style={{ minHeight: 400 }}>
                <ResourceContentViewer resource={currentLesson.resource} height={500} />
              </div>
              <div className="card-footer d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentIndex <= 0}
                  onClick={() => setCurrentLessonId(lessons[currentIndex - 1].id)}
                >
                  <i className="bi bi-arrow-left me-1" /> Previous
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={currentIndex >= lessons.length - 1}
                  onClick={() => setCurrentLessonId(lessons[currentIndex + 1].id)}
                >
                  Next Lesson <i className="bi bi-arrow-right ms-1" />
                </button>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-collection-play" style={{ fontSize: 48, color: '#ccc' }} />
                <h5 className="mt-3">Select a lesson to start</h5>
              </div>
            </div>
          )}
        </div>
      </div>

      {isOpenQuiz && currentLesson && (
        <QuizModal
          isOpen={isOpenQuiz}
          onClose={onCloseQuiz}
          lesson={currentLesson}
          onPassed={() => {
            // Al aprobar el quiz también se marca la lección como completada
            if (!isCompleted(currentLesson)) {
              handleMarkComplete(currentLesson)
            } else {
              refetch()
            }
          }}
        />
      )}
    </div>
  )
}
